import type { PlantState, PlantType, Season, SeasonEnvironment } from "../types";

export interface WinteringResult {
  plant: PlantState;
  healthLoss: number;
  enteredDormancy: boolean;
  survived: boolean;
}

export const SEASON_ENVIRONMENTS: Readonly<Record<Season, SeasonEnvironment>> = {
  spring: {
    season: "spring", averageTemperature: 15, daylightHours: 13, evaporationMultiplier: 0.9, growthMultiplier: 1.12, pestSpawnRate: 0.05, weedSpawnRate: 0.018,
    palette: { sky: "#bfe3ed", ambient: "#fff4d8", ground: "#88a968", foliage: "#65a765", accent: "#f3a7bd", shadow: "#58736f" },
  },
  summer: {
    season: "summer", averageTemperature: 28, daylightHours: 15, evaporationMultiplier: 1.55, growthMultiplier: 1.28, pestSpawnRate: 0.1, weedSpawnRate: 0.032,
    palette: { sky: "#79c8eb", ambient: "#fff1ba", ground: "#66964d", foliage: "#318b4b", accent: "#ffd45b", shadow: "#416777" },
  },
  autumn: {
    season: "autumn", averageTemperature: 17, daylightHours: 11, evaporationMultiplier: 0.82, growthMultiplier: 0.86, pestSpawnRate: 0.06, weedSpawnRate: 0.013,
    palette: { sky: "#adc7d5", ambient: "#ffd7a1", ground: "#9b8650", foliage: "#b06d3d", accent: "#df5c43", shadow: "#655b62" },
  },
  winter: {
    season: "winter", averageTemperature: 3, daylightHours: 9, evaporationMultiplier: 0.38, growthMultiplier: 0.32, pestSpawnRate: 0.02, weedSpawnRate: 0.002,
    palette: { sky: "#b8ccd9", ambient: "#eaf4f7", ground: "#9eaead", foliage: "#59776a", accent: "#d9eef4", shadow: "#60717e" },
  },
};

const ORDER: readonly Season[] = ["spring", "summer", "autumn", "winter"];

export class SeasonSystem {
  private season: Season;
  private readonly listeners = new Set<(previous: Season, current: Season, environment: SeasonEnvironment) => void>();

  constructor(initialSeason: Season = "spring") {
    this.season = initialSeason;
  }

  get current(): Season {
    return this.season;
  }

  get environment(): Readonly<SeasonEnvironment> {
    return SEASON_ENVIRONMENTS[this.season];
  }

  subscribe(listener: (previous: Season, current: Season, environment: SeasonEnvironment) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  transitionTo(season: Season): boolean {
    if (season === this.season) return false;
    const previous = this.season;
    this.season = season;
    for (const listener of this.listeners) listener(previous, season, SEASON_ENVIRONMENTS[season]);
    return true;
  }

  advance(): Season {
    const index = (ORDER.indexOf(this.season) + 1) % ORDER.length;
    this.transitionTo(ORDER[index] ?? "spring");
    return this.season;
  }

  getTemperature(weatherModifier = 0, minuteOfDay = 720): number {
    const dayPhase = Math.sin(((minuteOfDay - 360) / 1_440) * Math.PI * 2);
    return Math.round((this.environment.averageTemperature + dayPhase * 4 + weatherModifier) * 10) / 10;
  }

  isPlantInSeason(definition: PlantType): boolean {
    return definition.preferredSeasons.includes(this.season);
  }

  getPlantGrowthMultiplier(definition: PlantType): number {
    if (definition.preferredSeasons.includes(this.season)) return this.environment.growthMultiplier;
    if (definition.seasonsAvailable.includes(this.season)) return this.environment.growthMultiplier * 0.72;
    return definition.lifeCycle === "perennial" || definition.lifeCycle === "tree" ? 0.16 : 0.08;
  }

  evaluateWintering(plant: PlantState, definition: PlantType, temperature: number, elapsedDays = 1): WinteringResult {
    if (this.season !== "winter" || plant.stage === "withered") return { plant: { ...plant }, healthLoss: 0, enteredDormancy: false, survived: plant.health > 0 };
    const freezingSeverity = Math.max(0, (5 - temperature) / 15);
    const vulnerability = Math.max(0, 1 - definition.coldTolerance);
    const protection = (plant.mulched ? 0.45 : 0) + (plant.winterProtected ? 0.48 : 0);
    const healthLoss = Math.max(0, freezingSeverity * vulnerability * 18 * elapsedDays * (1 - Math.min(0.88, protection)));
    const health = Math.max(0, plant.health - healthLoss);
    const perennial = definition.lifeCycle === "perennial" || definition.lifeCycle === "tree";
    const enteredDormancy = perennial && health > 0 && !["seed", "withered", "dormant"].includes(plant.stage);
    const stage = health <= 0 ? "withered" : enteredDormancy ? "dormant" : plant.stage;
    return {
      // 休眠前の段階を覚えておく。春に sprout まで巻き戻すと、実った株が毎年振り出しに戻ってしまう。
      plant: { ...plant, health, coldDamage: Math.min(100, plant.coldDamage + healthLoss), stage, ...(enteredDormancy ? { dormantFromStage: plant.stage } : {}), stageProgress: enteredDormancy ? plant.stageProgress : plant.stageProgress },
      healthLoss,
      enteredDormancy,
      survived: health > 0,
    };
  }

  awakenFromDormancy(plant: PlantState): PlantState {
    if (this.season !== "spring" || plant.stage !== "dormant") return { ...plant };
    const resumed = plant.dormantFromStage ?? "sprout";
    return { ...plant, stage: resumed, coldDamage: Math.max(0, plant.coldDamage - 35), health: Math.min(100, plant.health + 12) };
  }
}
